import { useEffect, useRef, useState } from "react";
import { HorizontalDrawer, type HorizontalDrawerProps } from "./HorizontalDrawer";

/**
 * Base shape for draggable sidebar items.
 */
export interface IDraggableSideBarItemBase {
  id: string;
  label: string;
  data: object;
}

/**
 * Draggable sidebar item with typed payload data.
 */
export interface IDraggableSideBarItem<TData extends object = Record<string, unknown>> extends Omit<
  IDraggableSideBarItemBase,
  "data"
> {
  data: TData;
}

/**
 * Props for the draggable sidebar control.
 *
 * @param items - The list of items rendered as draggable entries
 * @param header - Optional non-scrollable header rendered above the item list
 * @param multiSelect - Optional. When true, enables multi-selection via shift/ctrl+click before dragging
 * @param onItemDragStart - Optional callback fired when item drag starts; receives the directly dragged item
 * @param onItemDragEnd - Optional callback fired when item drag ends
 * @param renderItem - Optional function to render a custom draggable element per item; receives the item and its selected state
 */
export interface DraggableSideBarProps<TData extends object = Record<string, unknown>> extends Omit<
  HorizontalDrawerProps,
  "children"
> {
  items: IDraggableSideBarItem<TData>[];
  header?: React.ReactNode;
  multiSelect?: boolean;
  onItemDragStart?: (
    item: IDraggableSideBarItem<TData>,
    event: React.DragEvent<HTMLLIElement>,
  ) => void;
  onItemDragEnd?: (
    item: IDraggableSideBarItem<TData>,
    event: React.DragEvent<HTMLLIElement>,
  ) => void;
  renderItem?: (item: IDraggableSideBarItem<TData>, selected: boolean) => React.ReactElement;
}

/**
 * Custom drag MIME type for sidebar items.
 */
const DRAG_ITEM_MIME_TYPE = "application/x-mosaic-draggable-sidebar-item";

/**
 * Creates a stacked drag preview element for multi-item drags.
 *
 * @param labels - Labels for the items included in the drag operation
 * @returns A detached DOM element suitable for DataTransfer.setDragImage
 */
const createMultiItemDragPreview = (labels: string[]): HTMLDivElement => {
  const previewRoot = document.createElement("div");
  previewRoot.setAttribute("data-mosaic-drag-preview", "true");
  previewRoot.style.position = "fixed";
  previewRoot.style.top = "-1000px";
  previewRoot.style.left = "-1000px";
  previewRoot.style.pointerEvents = "none";
  previewRoot.style.zIndex = "2147483647";

  const stack = document.createElement("div");
  stack.style.position = "relative";
  stack.style.width = "220px";
  stack.style.height = "48px";
  previewRoot.appendChild(stack);

  labels
    .slice(0, 3)
    .reverse()
    .forEach((label, index) => {
      const card = document.createElement("div");
      card.textContent = label;
      card.style.position = "absolute";
      card.style.inset = "0";
      card.style.transform = `translate(${index * 6}px, ${index * 6}px)`;
      card.style.padding = "10px 12px";
      card.style.border = "1px solid #0d6efd";
      card.style.borderRadius = "6px";
      card.style.backgroundColor = "#ffffff";
      card.style.boxShadow = "0 4px 10px rgba(13, 110, 253, 0.25)";
      card.style.color = "#0d6efd";
      card.style.fontSize = "12px";
      card.style.fontWeight = "600";
      card.style.whiteSpace = "nowrap";
      card.style.overflow = "hidden";
      card.style.textOverflow = "ellipsis";
      stack.appendChild(card);
    });

  return previewRoot;
};

/**
 * Default renderer for sidebar items (button element). Displays in primary colour when selected.
 *
 * @param item - The item to render
 * @param selected - Whether the item is currently selected
 * @returns A button element with the item label, styled to reflect selection state
 */
const defaultRenderItem = <TData extends object>(
  item: IDraggableSideBarItem<TData>,
  selected: boolean,
): React.ReactElement => (
  <button
    type="button"
    className={`btn ${selected ? "btn-primary" : "btn-outline-secondary"} text-start w-100`}
  >
    {item.label}
  </button>
);

/**
 * Sidebar control that renders draggable items in a vertical list. Supports multi-selection
 * via shift/ctrl+click when `multiSelect` is enabled; dragging a selected item will include
 * all currently selected items in the drag payload.
 *
 * @param props - The component props
 * @param props.header - Optional non-scrollable header rendered above the item list
 * @param props.items - The list of items rendered as draggable entries
 * @param props.multiSelect - Optional. When true, enables multi-selection via shift/ctrl+click
 * @param props.onItemDragEnd - Optional callback fired when item drag ends
 * @param props.onItemDragStart - Optional callback fired when item drag starts; receives the directly dragged item
 * @param props.renderItem - Optional function to render a custom draggable element per item; receives item and selected state
 * @param props.style - Additional inline styles for the drawer container
 * @returns A draggable sidebar wrapped in HorizontalDrawer
 */
export const DraggableSideBar = <TData extends object = Record<string, unknown>>({
  header,
  items,
  multiSelect = false,
  onItemDragEnd,
  onItemDragStart,
  renderItem = defaultRenderItem,
  style,
  ...drawerProps
}: DraggableSideBarProps<TData>): React.ReactElement | null => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const itemsById = new Map(items.map((item) => [item.id, item]));

  // Clean up drag preview element on unmount to prevent orphaned DOM nodes
  useEffect(() => {
    return () => {
      dragPreviewRef.current?.remove();
      dragPreviewRef.current = null;
    };
  }, []);

  // Derive effective selection: items are only considered selected when multiSelect is enabled
  const effectiveSelectedIds = multiSelect ? selectedIds : [];

  /**
   * Handles item click for selection, supporting shift and ctrl/meta modifier keys.
   * - Plain click selects only the clicked item and sets it as the range anchor.
   * - Ctrl/Meta+click toggles the clicked item's selection and updates the anchor.
   * - Shift+click selects the range between the anchor and the clicked item.
   *
   * @param item - The item that was clicked
   * @param event - The originating mouse event
   */
  const handleItemClick = (item: IDraggableSideBarItem<TData>, event: React.MouseEvent): void => {
    if (!multiSelect) return;

    if (event.shiftKey && anchorId !== null) {
      const anchorIndex = items.findIndex((i) => i.id === anchorId);
      const currentIndex = items.findIndex((i) => i.id === item.id);
      const start = Math.min(anchorIndex, currentIndex);
      const end = Math.max(anchorIndex, currentIndex);
      const rangeIdsUnordered = items.slice(start, end + 1).map((i) => i.id);
      const rangeIds =
        currentIndex >= anchorIndex ? rangeIdsUnordered : rangeIdsUnordered.reverse();

      if (event.ctrlKey || event.metaKey) {
        // Ctrl/Meta + Shift expands selection without removing previously selected items.
        setSelectedIds((previousSelectedIds) => [
          ...previousSelectedIds,
          ...rangeIds.filter((rangeId) => !previousSelectedIds.includes(rangeId)),
        ]);
      } else {
        setSelectedIds(rangeIds);
      }
    } else if (event.ctrlKey || event.metaKey) {
      setSelectedIds((prev) =>
        prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id],
      );
      setAnchorId(item.id);
    } else {
      setSelectedIds([item.id]);
      setAnchorId(item.id);
    }
  };

  return (
    <HorizontalDrawer
      {...drawerProps}
      style={{
        overflowY: "auto",
        backgroundColor: "var(--bs-body-bg)",
        border: "1px solid var(--bs-border-color)",
        ...style,
      }}
    >
      <div
        className="d-flex flex-column"
        style={{ height: "100%" }}
      >
        {header !== undefined && <div className="p-2 border-bottom flex-shrink-0">{header}</div>}
        <div
          className="small-scrollbar flex-grow-1 p-2 overflow-auto"
          style={{ minHeight: 0 }}
        >
          <ul className="list-unstyled d-flex flex-column gap-2 m-0">
            {items.map((item) => {
              const isSelected = effectiveSelectedIds.includes(item.id);
              const itemsToDrag =
                isSelected && effectiveSelectedIds.length > 1
                  ? effectiveSelectedIds
                      .map((selectedId) => itemsById.get(selectedId))
                      .filter(
                        (selectedItem): selectedItem is IDraggableSideBarItem<TData> =>
                          selectedItem !== undefined,
                      )
                  : [item];

              return (
                <li
                  key={item.id}
                  draggable={true}
                  onClick={(event) => handleItemClick(item, event)}
                  onDragStart={(event) => {
                    if (multiSelect && !isSelected) {
                      setSelectedIds([item.id]);
                      setAnchorId(item.id);
                    }

                    event.dataTransfer.effectAllowed = "copy";
                    const payload = JSON.stringify({ data: itemsToDrag.map((i) => i.data) });
                    event.dataTransfer.setData(
                      "text/plain",
                      itemsToDrag.map((i) => i.label).join(", "),
                    );
                    event.dataTransfer.setData("application/json", payload);
                    event.dataTransfer.setData(DRAG_ITEM_MIME_TYPE, payload);

                    const dragPreview = createMultiItemDragPreview(
                      itemsToDrag.map((draggedItem) => draggedItem.label),
                    );
                    if (itemsToDrag.length > 1) {
                      dragPreviewRef.current?.remove();
                      dragPreviewRef.current = dragPreview;
                      document.body.appendChild(dragPreview);
                      event.dataTransfer.setDragImage(dragPreview, 24, 24);
                    }
                    onItemDragStart?.(item, event);
                  }}
                  onDragEnd={(event) => {
                    dragPreviewRef.current?.remove();
                    dragPreviewRef.current = null;
                    onItemDragEnd?.(item, event);
                  }}
                >
                  {renderItem(item, isSelected)}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </HorizontalDrawer>
  );
};

DraggableSideBar.displayName = "DraggableSideBar";
